/* -------------------------------------------------------------------------- */
/*  Procore – Budget Line Items CSV export                                    */
/* -------------------------------------------------------------------------- *
 *  - Output matches Procore's "Budget Line Items" import template            *
 *  - Generic across tenants: caller specifies which sort fields supply the   *
 *    Procore Cost Code and Cost Type values for line items and markups       *
 *  - Two Cost Type tiers, controlled by costTypeSortField:                   *
 *      • Single  — one sort field used for all LMSEO buckets                 *
 *      • Manifold — one sort field per LMSEO bucket, so a single line item   *
 *        with multiple non-zero LMSEO costs splits across multiple Cost      *
 *        Types. All-zero LMSEO with a non-zero total falls back to "other".  *
 *  - Groups by (Cost Code, Cost Type) and sums Budget Amount                 *
 *  - Description is the Cost Code description from the sort field            *
 *  - $0 line items and markups are omitted                                   *
 *  - Missing Cost Code / Cost Type sort field values throw ValidationError   *
 *  - Returns [stringLines, "csv"]                                            */
/* -------------------------------------------------------------------------- */

import { getEstimateReportData } from "../../excel/workbook";
import { ValidationError } from "../../errors";
import { getProcessedEstimate } from "../../excel/datasources/estimate";

/* ───────────────────────── 1. Types & constants ─────────────────────────── */

export interface ProcoreCostTypeManifold {
  labor: string;
  material: string;
  equip: string;
  sub: string;
  other: string;
}

export interface ProcoreExportOptions {
  // Sort field name used for Procore Cost Code on line items and markups.
  costCodeSortField: string;
  // Cost Type sort field configuration. A single string applies one sort field
  // across all LMSEO buckets (single tier); a ProcoreCostTypeManifold object
  // assigns a separate sort field per bucket (manifold tier).
  costTypeSortField: string | ProcoreCostTypeManifold;
  // Optional dedicated Cost Type sort field for markups. Falls back to
  // costTypeSortField in single tier, or costTypeSortField.other in manifold tier.
  markupCostTypeSortField?: string;
}

const HEADERS = [
  "Cost Code",
  "Cost Type",
  "Description",
  "Manual Calculation",
  "Unit Qty",
  "UOM",
  "Unit Cost",
  "Budget Amount",
  "Start Date",
  "End Date",
  "Curve",
];

type LmseoBucket = keyof ProcoreCostTypeManifold;

const LMSEO_BUCKETS: { kind: LmseoBucket; ucKey: string }[] = [
  { kind: "labor", ucKey: "labor_uc" },
  { kind: "material", ucKey: "material_uc" },
  { kind: "equip", ucKey: "equip_uc" },
  { kind: "sub", ucKey: "sub_uc" },
  { kind: "other", ucKey: "other_uc" },
];

interface GroupedRow {
  costCode: string;
  costCodeDesc: string;
  costType: string;
  amount: number;
  qtySum: number;
  uoms: Set<string>;
  hasMarkup: boolean;
}

/* ───────────────────────── 2. Helpers ───────────────────────────────────── */

const csvEscape = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const formatBudgetRow = (row: GroupedRow): string => {
  // Use actual qty/UOM only when grouped line items share a single UOM and no markup
  // dollars are mixed in. Otherwise fall back to 1 / LS so the Budget Amount stays correct.
  const sharedUom = !row.hasMarkup && row.uoms.size === 1 ? Array.from(row.uoms)[0] : "";
  const useActuals = sharedUom !== "" && row.qtySum > 0;

  const unitQty = useActuals ? row.qtySum : 1;
  const uom = useActuals ? sharedUom : "LS";
  const unitCost = useActuals ? row.amount / row.qtySum : row.amount;

  return [
    csvEscape(row.costCode),
    csvEscape(row.costType),
    csvEscape(row.costCodeDesc),
    "True",
    csvEscape(Number(unitQty.toFixed(4))),
    csvEscape(uom),
    csvEscape(Number(unitCost.toFixed(2))),
    csvEscape(Number(row.amount.toFixed(2))),
    "",
    "",
    "",
  ].join(",");
};

/* ───────────────────────── 3. Export function ───────────────────────────── */

export async function createProcoreExport(opts: ProcoreExportOptions): Promise<[string[], string]> {
  const reportData = await getEstimateReportData();
  const estimateId = reportData[0]?.estimate_id as string;
  if (!estimateId) throw new ValidationError("Load an estimate before exporting.");
  const processed = await getProcessedEstimate(estimateId);
  const estimate = processed?.baseBidItems ?? [];

  const codeKey = `${opts.costCodeSortField}_code`;
  const codeDescKey = `${opts.costCodeSortField}_desc`;

  const isManifold = typeof opts.costTypeSortField !== "string";
  const manifoldFields = isManifold ? (opts.costTypeSortField as ProcoreCostTypeManifold) : null;
  const singleTypeKey = !isManifold ? `${opts.costTypeSortField as string}_code` : null;

  const markupTypeField =
    opts.markupCostTypeSortField ??
    (isManifold ? (opts.costTypeSortField as ProcoreCostTypeManifold).other : (opts.costTypeSortField as string));
  const markupTypeKey = `${markupTypeField}_code`;

  interface MissingFieldRow {
    name: string;
    field: string;
  }
  const missingFields: MissingFieldRow[] = [];
  const groups = new Map<string, GroupedRow>();

  const upsert = (
    costCode: string,
    costCodeDesc: string,
    costType: string,
    amount: number,
    quantity: number,
    uom: string,
    isMarkup: boolean
  ) => {
    // \x1F (unit separator) avoids collisions if a sort field value contains spaces.
    const key = `${costCode}\x1F${costType}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        costCode,
        costCodeDesc,
        costType,
        amount: 0,
        qtySum: 0,
        uoms: new Set<string>(),
        hasMarkup: false,
      };
      groups.set(key, group);
    }
    group.amount += amount;
    group.qtySum += quantity || 0;
    if (uom) group.uoms.add(uom);
    if (isMarkup) group.hasMarkup = true;
  };

  for (const row of estimate) {
    if (row.type === "line_item") {
      const total = Number(row.total) || 0;
      if (total === 0) continue; // omit zero-dollar items

      const costCode = (row[codeKey] ?? "").toString().trim();
      const itemName = row.name || "(unnamed line item)";
      if (!costCode) {
        missingFields.push({ name: itemName, field: opts.costCodeSortField });
        continue;
      }

      const codeDesc = (row[codeDescKey] ?? "").toString();
      const quantity = Number(row.quantity) || 0;
      const uom = (row.uom ?? "").toString();

      if (manifoldFields) {
        // Determine which LMSEO buckets carry cost; fall back to a single "other"
        // bucket using total_uc when all LMSEO are zero (typical for text overrides).
        const activeBuckets = LMSEO_BUCKETS.map((b) => ({ kind: b.kind, uc: Number(row[b.ucKey]) || 0 })).filter(
          (b) => b.uc !== 0
        );

        const splits =
          activeBuckets.length > 0 ? activeBuckets : [{ kind: "other" as LmseoBucket, uc: Number(row.total_uc) || 0 }];

        for (const split of splits) {
          const sortFieldName = manifoldFields[split.kind];
          const costType = (row[`${sortFieldName}_code`] ?? "").toString().trim();
          if (!costType) {
            missingFields.push({ name: itemName, field: sortFieldName });
            continue;
          }
          const amount = quantity * split.uc;
          if (amount === 0) continue;
          upsert(costCode, codeDesc, costType, amount, quantity, uom, false);
        }
      } else {
        const costType = (row[singleTypeKey as string] ?? "").toString().trim();
        if (!costType) {
          missingFields.push({ name: itemName, field: opts.costTypeSortField as string });
          continue;
        }
        upsert(costCode, codeDesc, costType, total, quantity, uom, false);
      }
    } else if (row.type === "markup" || row.type === "atl_markup") {
      const total = Number(row.total) || 0;
      if (total === 0) continue;

      const costCode = (row[codeKey] ?? "").toString().trim();
      const costType = (row[markupTypeKey] ?? "").toString().trim();
      const markupName = row.name || "(unnamed markup)";
      if (!costCode) {
        missingFields.push({ name: markupName, field: opts.costCodeSortField });
        continue;
      }
      if (!costType) {
        missingFields.push({ name: markupName, field: markupTypeField });
        continue;
      }

      upsert(costCode, (row[codeDescKey] ?? "").toString(), costType, total, 0, "", true);
    }
  }

  if (missingFields.length > 0) {
    const display = missingFields
      .slice(0, 5)
      .map((m) => `${m.name} (${m.field})`)
      .join(", ");
    const remaining = missingFields.length - 5;
    const suffix = remaining > 0 ? `, and ${remaining} more` : "";
    throw new ValidationError(
      `${missingFields.length} item(s) missing required Procore sort field values: ${display}${suffix}`
    );
  }

  const lines: string[] = [HEADERS.map(csvEscape).join(",")];
  const sortedKeys = Array.from(groups.keys()).sort();
  for (const key of sortedKeys) {
    lines.push(formatBudgetRow(groups.get(key) as GroupedRow));
  }

  return [lines, "csv"];
}
